import { useContext, useMemo } from "react";
import { useWorkflowFields } from "@repo/shared-components";
import { Spinner, ImperativeDialogContext, DialogHeader, DialogTitle } from "@repo/ui";
import { useActionMutations } from "~/pages/administration/workflows-page/hooks/apis/use-action-mutations";
import { useLinkActionStatus } from "~/pages/administration/workflows-page/hooks/apis/use-link-action-mutations";
import { useWorkflowStatusMutations } from "~/pages/administration/workflows-page/hooks/apis/use-workflow-status-mutations";
import { useWorkflowCreationContext } from "~/pages/administration/workflows-page/workflow-creation-context-provider";
import { WorkflowsDialogFooter } from "../workflows-dialog-footer";
import { ActionInput } from "./action-input";
import { ActionStatusLinkDialog } from "./action-status-link-dialog";
import { ActionTagsPanel } from "./action-tags-panel";
import type { PathField } from "./utils/path-field-meta";
import { WorkflowsTable, type TableColumn } from "../workflows-table/workflows-table";
import { useActionView, type ActionRow } from "./hooks/use-action-view";

type Props = {
  isEditMode?: boolean;
  workflowId?: number;
  onClose?: () => void;
};

const tableColumns: TableColumn<ActionRow>[] = [{ label: "Action", key: "name" }];

export const ActionView = ({ isEditMode = false, workflowId, onClose }: Props) => {
  const {
    state: { fields: creationFields, statuses: contextStatuses },
    updateState,
  } = useWorkflowCreationContext();
  const dialogContext = useContext(ImperativeDialogContext);

  const {
    actions,
    isLoading: isLoadingActions,
    createActions,
    isCreating,
    updateAction,
    isUpdating,
    deleteAction,
    isDeleting,
    refetch,
    isTogglingStatuses,
  } = useActionMutations(isEditMode && workflowId ? [workflowId] : []);
  const { linkStatus } = useLinkActionStatus();

  const { statuses: apiStatuses } = useWorkflowStatusMutations(workflowId, isEditMode);

  const { fields: workflowFields, isLoading: isLoadingWorkflowFields } = useWorkflowFields(
    workflowId,
    isEditMode
  );

  const actionPathFields = useMemo<PathField[]>(() => {
    const fields = isEditMode ? workflowFields : creationFields;
    return fields.flatMap((field) => {
      if (field.type !== "Directory" || !("id" in field) || typeof field.id !== "number") {
        return [];
      }

      return [field];
    });
  }, [creationFields, isEditMode, workflowFields]);

  const dynamicPresetFields = useMemo<PathField[]>(() => {
    const fields = isEditMode ? workflowFields : creationFields;
    return fields.flatMap((field) => {
      if (!("id" in field) || typeof field.id !== "number") return [];
      const nameMatch = field.name.toLowerCase().includes("preset");
      if (!nameMatch) return [];
      if (field.type === "String") return [field];
      return [];
    });
  }, [creationFields, isEditMode, workflowFields]);

  const fieldNames = useMemo(() => {
    if (isEditMode && workflowFields.length > 0) {
      return workflowFields.map((f) => f.name);
    }
    return creationFields.map((f) => f.name);
  }, [isEditMode, workflowFields, creationFields]);

  const linkableStatuses = useMemo(
    () => (isEditMode ? apiStatuses : contextStatuses).map((s) => ({ code: s.code, name: s.name })),
    [isEditMode, apiStatuses, contextStatuses]
  );

  const {
    data,
    selectedAction,
    selectedIndex,
    setSelectedIndex,
    handleOnClickAdd,
    handleInputSave,
    handleDelete,
    handleReorder,
    handleSave,
    handleBack,
    hasChanges,
    hasValidationErrors,
  } = useActionView({
    editModeActions: isEditMode ? actions : undefined,
    isEditMode,
    workflowId,
    updateAction,
    deleteAction,
    createActions,
    linkStatus,
    refetch,
    onClose,
  });

  const isSelectedActionPersisted = useMemo(
    () => selectedAction?.id != null && (actions ?? []).some((a) => a.id === selectedAction.id),
    [selectedAction, actions]
  );

  const handleManageStatuses = () => {
    if (selectedAction == null || selectedIndex == null) return;
    updateState("activeActionId", selectedAction.id ?? null);
    dialogContext?.showDialog({
      content: (
        <div className="flex flex-col gap-6">
          <DialogHeader>
            <DialogTitle>Link Statuses - {selectedAction.name}</DialogTitle>
          </DialogHeader>
          <ActionStatusLinkDialog
            action={selectedAction}
            actionIndex={selectedIndex}
            statuses={linkableStatuses}
            isEditMode={isEditMode}
            isPersisted={isSelectedActionPersisted}
            workflowId={workflowId}
            onRefetch={refetch}
            onClose={() => dialogContext.closeDialog()}
          />
        </div>
      ),
      contentClassName: "max-w-3xl",
    });
  };

  const dropdownMenuOptions = [
    {
      id: "manage-statuses",
      label: "Manage Statuses",
      onClick: handleManageStatuses,
    },
    {
      id: "delete",
      label: "Delete Action",
      onClick: handleDelete,
    },
  ];

  if (isEditMode && (isLoadingActions || isLoadingWorkflowFields)) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="flex max-h-[calc(90vg-12rem)] min-h-0 w-full flex-1 gap-4 overflow-y-auto pr-1 max-md:flex-col">
        <WorkflowsTable
          columns={tableColumns}
          data={data}
          onClickAdd={handleOnClickAdd}
          selectedIndex={selectedIndex}
          onSelectRow={setSelectedIndex}
          addButtonLabel="Create"
          className="w-full"
          dropdownMenuItems={dropdownMenuOptions}
          isSortable
          onReorder={handleReorder}
        />
        <ActionInput
          key={selectedAction ? `action-${selectedIndex}` : "empty"}
          action={selectedAction}
          mode={isEditMode ? "edit" : "create"}
          pathFields={actionPathFields}
          dynamicPresetFields={dynamicPresetFields}
          onSave={handleInputSave}
        />
        <ActionTagsPanel fieldNames={fieldNames} />
      </div>
      <WorkflowsDialogFooter
        onCancel={handleBack}
        onSave={handleSave}
        loading={isUpdating || isDeleting || isCreating || isTogglingStatuses}
        disableSave={
          !hasChanges ||
          hasValidationErrors ||
          isUpdating ||
          isDeleting ||
          isCreating ||
          isTogglingStatuses
        }
      />
    </div>
  );
};
